import { http } from "./http";

function parseApiError(error) {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data?.error) return String(data.error);
  if (data?.message) return String(data.message);
  const status = error?.response?.status;
  if (status === 403) {
    return "Acesso negado. Seu Firebase UID precisa estar em BILLING_ADMIN_USER_IDS no Billing-Service.";
  }
  if (status === 401) return "Não autenticado. Faça login novamente.";
  return error?.message || "Erro na API de billing.";
}

function billingCompanyHeader(companyId) {
  const id = String(companyId || "").trim();
  if (!id) throw new Error("companyId inválido.");
  return { "X-Company-ID": id };
}

export async function getSubscription(companyId) {
  try {
    const { data } = await http.get("/api/billing/subscription", {
      headers: billingCompanyHeader(companyId),
    });
    return data;
  } catch (error) {
    throw new Error(parseApiError(error));
  }
}

/**
 * GET /api/billing/admin/companies — todas as empresas com o plano efetivo.
 * Só para quem pode alterar planos (BILLING_ADMIN_USER_IDS); demais recebem 403.
 * Retorna null quando o usuário não tem essa permissão.
 */
export async function listAllCompaniesForPlanAdmin() {
  try {
    const { data } = await http.get("/api/billing/admin/companies");
    return Array.isArray(data) ? data : [];
  } catch (error) {
    if (error?.response?.status === 403) return null;
    throw new Error(parseApiError(error));
  }
}

/** GET /api/billing/admin/companies/{companyId} — plano efetivo de uma empresa (admin de billing). */
export async function getCompanyPlanAsAdmin(companyId) {
  const id = String(companyId || "").trim();
  if (!id) throw new Error("companyId inválido.");
  try {
    const { data } = await http.get(`/api/billing/admin/companies/${encodeURIComponent(id)}`);
    return data;
  } catch (error) {
    throw new Error(parseApiError(error));
  }
}

/**
 * GET /api/billing/admin/entitlement-overrides?companyId=
 * Retorna { planCode, baseLimits, effectiveLimits, overrides? } (admin de billing).
 */
export async function getEntitlementOverrides(companyId) {
  const id = String(companyId || "").trim();
  if (!id) throw new Error("companyId inválido.");
  try {
    const { data } = await http.get(
      `/api/billing/admin/entitlement-overrides?companyId=${encodeURIComponent(id)}`
    );
    return data;
  } catch (error) {
    throw new Error(parseApiError(error));
  }
}

/**
 * PUT /api/billing/admin/entitlement-overrides
 * patch: { maxContacts: 200, ... } — null volta o campo ao padrão do plano; -1 = ilimitado.
 */
export async function upsertEntitlementOverrides(companyId, patch, reason) {
  const id = String(companyId || "").trim();
  if (!id) throw new Error("companyId inválido.");
  const body = { companyId: id, ...patch };
  const trimmedReason = reason?.trim();
  if (trimmedReason) body.reason = trimmedReason;
  try {
    const { data } = await http.put("/api/billing/admin/entitlement-overrides", body);
    return data;
  } catch (error) {
    throw new Error(parseApiError(error));
  }
}

/** DELETE /api/billing/admin/entitlement-overrides — remove todos os ajustes da empresa. */
export async function deleteEntitlementOverrides(companyId) {
  const id = String(companyId || "").trim();
  if (!id) throw new Error("companyId inválido.");
  try {
    await http.delete(
      `/api/billing/admin/entitlement-overrides?companyId=${encodeURIComponent(id)}`
    );
  } catch (error) {
    throw new Error(parseApiError(error));
  }
}

/**
 * POST /api/billing/admin/plan-grant
 * Body: { companyId, planCode, durationDays, reason? }
 * Headers: Authorization, X-Firebase-UID (gateway também injeta o UID do token)
 */
export async function grantPlan({ companyId, planCode, durationDays, reason }) {
  const normalizedCompanyId = String(companyId || "").trim();
  const normalizedPlanCode = String(planCode || "").trim().toLowerCase();
  const days = Number(durationDays);

  if (!normalizedCompanyId) {
    throw new Error("companyId é obrigatório.");
  }
  if (!["starter", "essential", "pro"].includes(normalizedPlanCode)) {
    throw new Error("planCode deve ser starter, essential ou pro.");
  }
  if (!Number.isFinite(days) || days < 1 || days > 365) {
    throw new Error("durationDays deve estar entre 1 e 365.");
  }

  const body = {
    companyId: normalizedCompanyId,
    planCode: normalizedPlanCode,
    durationDays: days,
  };
  const trimmedReason = reason?.trim();
  if (trimmedReason) body.reason = trimmedReason;

  try {
    const { data } = await http.post("/api/billing/admin/plan-grant", body);
    return data;
  } catch (error) {
    throw new Error(parseApiError(error));
  }
}

export async function revokePlanGrant(companyId) {
  const normalizedCompanyId = String(companyId || "").trim();
  if (!normalizedCompanyId) {
    throw new Error("companyId é obrigatório.");
  }
  try {
    await http.delete(
      `/api/billing/admin/plan-grant?companyId=${encodeURIComponent(normalizedCompanyId)}`
    );
  } catch (error) {
    throw new Error(parseApiError(error));
  }
}

